import { Sword, Eye, BookOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { CharacterArchetype } from '@/lib/game-logic/templates';

const KIT_ICONS: Record<string, React.ElementType> = {
  blade: Sword,
  shadow: Eye,
  sage: BookOpen,
};

const STAT_COLORS: Record<string, string> = {
  brawn: 'bg-red-500',
  grace: 'bg-green-500',
  wits: 'bg-blue-500',
  spirit: 'bg-purple-500',
};

const STAT_LABELS: Record<string, string> = {
  brawn: 'BRN',
  grace: 'GRC',
  wits: 'WIT',
  spirit: 'SPR',
};

interface CharacterCardProps {
  archetype: CharacterArchetype;
  selected: boolean;
  onClick: () => void;
}

export function CharacterCard({ archetype, selected, onClick }: CharacterCardProps) {
  const Icon = KIT_ICONS[archetype.kit] || Sword;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'p-4 rounded-lg border text-left transition-colors w-full',
        selected
          ? 'bg-white/5 border-amber-500/40 ring-1 ring-amber-500/20'
          : 'bg-neutral-900 border-neutral-800 text-neutral-400 hover:border-neutral-700'
      )}
    >
      {/* Kit icon + class label */}
      <div className="flex items-center gap-2 mb-2">
        <Icon className={cn('w-4 h-4', selected ? 'text-amber-400' : 'text-neutral-500')} />
        <span className="text-xs font-medium uppercase tracking-wide text-neutral-500">
          {archetype.classLabel}
        </span>
      </div>

      {/* Character name */}
      <div className="font-semibold text-white mb-1">{archetype.name}</div>

      {/* Flavor text */}
      <p className="text-sm text-neutral-400 mb-3 line-clamp-2">{archetype.flavor}</p>

      {/* Stat bars */}
      <div className="space-y-1.5 mb-3">
        {(Object.entries(archetype.stats) as [string, number][]).map(([stat, value]) => (
          <div key={stat} className="flex items-center gap-2">
            <span className="text-[10px] font-medium text-neutral-500 w-7">{STAT_LABELS[stat]}</span>
            <div className="flex-1 h-1.5 bg-neutral-800 rounded-full overflow-hidden">
              <div
                className={cn('h-full rounded-full', STAT_COLORS[stat])}
                style={{ width: `${(value / 3) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Equipment pills */}
      <div className="flex flex-wrap gap-1">
        {archetype.equipment.map((item) => (
          <span
            key={item}
            className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-neutral-500"
          >
            {item}
          </span>
        ))}
      </div>
    </button>
  );
}
